"use client"

import * as React from 'react'
import { z } from 'zod'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { createCrudFormError } from '@open-mercato/ui/backend/utils/serverErrors'
import type { LocationRecord } from '../lib/locationPresentation'
import {
  buildLocationCapabilityOptions,
  buildLocationTypeOptions,
  buildSupportedModeOptions,
} from '../lib/locationPresentation'
import { uniqueStringArray } from '../lib/locationOptions'

export type LocationFormValues = {
  id?: string
  name: string
  code?: string
  type: string
  unlocode?: string
  addressStreet: string
  addressCity: string
  addressPostalCode: string
  addressCountry: string
  addressRegion?: string
  latitude?: number
  longitude?: number
  supportedModes: string[]
  capabilities: string[]
  geofenceRadiusM?: number
  slotBookingEnabled?: boolean
  avgHandlingTimeMin?: number
  contactName?: string
  contactPhone?: string
  gateInstructions?: string
  operatingHoursJson?: string
  isActive?: boolean
}

export type LocationCrudFormProps = {
  title: string
  submitLabel?: string
  backHref: string
  cancelHref: string
  initialValues: Partial<LocationFormValues>
  onSubmit: (values: LocationFormValues) => Promise<void>
  onDelete?: () => Promise<void>
  isLoading?: boolean
  loadingMessage?: string
}

const locationFormSchema: z.ZodType<LocationFormValues> = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().min(1, 'Name is required.'),
  code: z.string().optional(),
  type: z.string().trim().min(1, 'Location type is required.'),
  unlocode: z.string().optional(),
  addressStreet: z.string().trim().min(1, 'Street is required.'),
  addressCity: z.string().trim().min(1, 'City is required.'),
  addressPostalCode: z.string().trim().min(1, 'Postal code is required.'),
  addressCountry: z.string().trim().min(1, 'Country is required.'),
  addressRegion: z.string().optional(),
  latitude: z.number().min(-90, 'Latitude must be between -90 and 90.').max(90, 'Latitude must be between -90 and 90.'),
  longitude: z.number().min(-180, 'Longitude must be between -180 and 180.').max(180, 'Longitude must be between -180 and 180.'),
  supportedModes: z.array(z.string()).min(1, 'Select at least one supported mode.'),
  capabilities: z.array(z.string()).default([]),
  geofenceRadiusM: z.number().int().positive().max(200000).optional(),
  slotBookingEnabled: z.boolean().optional(),
  avgHandlingTimeMin: z.number().int().positive().max(10080).optional(),
  contactName: z.string().optional(),
  contactPhone: z.string().optional(),
  gateInstructions: z.string().optional(),
  operatingHoursJson: z.string().optional(),
  isActive: z.boolean().optional(),
})

function asNullableTrimmed(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function asNullableNumber(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return value
}

function parseOperatingHours(
  value: string | undefined,
  t: (key: string, fallback?: string) => string,
): Record<string, { open?: string | null; close?: string | null }> | null {
  const trimmed = typeof value === 'string' ? value.trim() : ''
  if (!trimmed) return null

  try {
    const parsed = JSON.parse(trimmed)
    if (parsed === null) return null
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('invalid_shape')
    }
    return parsed as Record<string, { open?: string | null; close?: string | null }>
  } catch {
    const message = t(
      'locations.form.errors.operatingHoursInvalid',
      'Operating hours must be valid JSON object data.',
    )
    throw createCrudFormError(message, { operatingHoursJson: message })
  }
}

export function buildLocationPayload(
  values: LocationFormValues,
  t: (key: string, fallback?: string) => string,
  options: { id?: string } = {},
): Record<string, unknown> {
  const name = values.name.trim()
  if (!name) {
    const message = t('locations.form.errors.nameRequired', 'Name is required.')
    throw createCrudFormError(message, { name: message })
  }

  const type = values.type.trim()
  if (!type) {
    const message = t('locations.form.errors.typeRequired', 'Location type is required.')
    throw createCrudFormError(message, { type: message })
  }

  const supportedModes = uniqueStringArray(Array.isArray(values.supportedModes) ? values.supportedModes : [])
  if (!supportedModes.length) {
    const message = t('locations.form.errors.supportedModesRequired', 'Select at least one supported mode.')
    throw createCrudFormError(message, { supportedModes: message })
  }

  const addressStreet = values.addressStreet.trim()
  const addressCity = values.addressCity.trim()
  const addressPostalCode = values.addressPostalCode.trim()
  const addressCountry = values.addressCountry.trim()
  if (!addressStreet || !addressCity || !addressPostalCode || !addressCountry) {
    const message = t('locations.form.errors.addressRequired', 'Address fields are required.')
    const fieldErrors: Record<string, string> = {}
    if (!addressStreet) fieldErrors.addressStreet = message
    if (!addressCity) fieldErrors.addressCity = message
    if (!addressPostalCode) fieldErrors.addressPostalCode = message
    if (!addressCountry) fieldErrors.addressCountry = message
    throw createCrudFormError(message, fieldErrors)
  }

  return {
    ...(options.id ? { id: options.id } : {}),
    name,
    code: asNullableTrimmed(values.code),
    type,
    unlocode: asNullableTrimmed(values.unlocode)?.toUpperCase() ?? null,
    address: {
      street: addressStreet,
      city: addressCity,
      postalCode: addressPostalCode,
      country: addressCountry.toUpperCase(),
      region: asNullableTrimmed(values.addressRegion),
    },
    latitude: values.latitude,
    longitude: values.longitude,
    supportedModes,
    capabilities: uniqueStringArray(Array.isArray(values.capabilities) ? values.capabilities : []),
    geofenceRadiusM: asNullableNumber(values.geofenceRadiusM),
    slotBookingEnabled: values.slotBookingEnabled ?? false,
    avgHandlingTimeMin: asNullableNumber(values.avgHandlingTimeMin),
    contactName: asNullableTrimmed(values.contactName),
    contactPhone: asNullableTrimmed(values.contactPhone),
    gateInstructions: asNullableTrimmed(values.gateInstructions),
    operatingHours: parseOperatingHours(values.operatingHoursJson, t),
    isActive: values.isActive ?? true,
  }
}

export function toLocationFormValues(record: LocationRecord): LocationFormValues {
  return {
    id: record.id,
    name: record.name,
    code: record.code ?? '',
    type: record.type ?? '',
    unlocode: record.unlocode ?? '',
    addressStreet: record.address.street,
    addressCity: record.address.city,
    addressPostalCode: record.address.postalCode,
    addressCountry: record.address.country,
    addressRegion: record.address.region ?? '',
    latitude: record.latitude ?? undefined,
    longitude: record.longitude ?? undefined,
    supportedModes: record.supportedModes,
    capabilities: record.capabilities,
    geofenceRadiusM: record.geofenceRadiusM ?? undefined,
    slotBookingEnabled: record.slotBookingEnabled,
    avgHandlingTimeMin: record.avgHandlingTimeMin ?? undefined,
    contactName: record.contactName ?? '',
    contactPhone: record.contactPhone ?? '',
    gateInstructions: record.gateInstructions ?? '',
    operatingHoursJson: record.operatingHours ? JSON.stringify(record.operatingHours, null, 2) : '',
    isActive: record.isActive,
  }
}

export function LocationCrudForm(props: LocationCrudFormProps) {
  const {
    title,
    submitLabel,
    backHref,
    cancelHref,
    initialValues,
    onSubmit,
    onDelete,
    isLoading,
    loadingMessage,
  } = props
  const t = useT()

  const typeOptions = React.useMemo(() => buildLocationTypeOptions(t), [t])
  const modeOptions = React.useMemo(() => buildSupportedModeOptions(t), [t])
  const capabilityOptions = React.useMemo(() => buildLocationCapabilityOptions(t), [t])

  const fields = React.useMemo<CrudField[]>(() => [
    { id: 'name', label: t('locations.form.fields.name', 'Name'), type: 'text', required: true },
    { id: 'code', label: t('locations.form.fields.code', 'Code'), type: 'text' },
    { id: 'unlocode', label: t('locations.form.fields.unlocode', 'UN/LOCODE'), type: 'text' },
    {
      id: 'type',
      label: t('locations.form.fields.type', 'Location type'),
      type: 'select',
      options: typeOptions,
      required: true,
    },
    { id: 'addressStreet', label: t('locations.form.fields.addressStreet', 'Street'), type: 'text', required: true },
    { id: 'addressCity', label: t('locations.form.fields.addressCity', 'City'), type: 'text', required: true },
    {
      id: 'addressPostalCode',
      label: t('locations.form.fields.addressPostalCode', 'Postal code'),
      type: 'text',
      required: true,
    },
    { id: 'addressCountry', label: t('locations.form.fields.addressCountry', 'Country'), type: 'text', required: true },
    { id: 'addressRegion', label: t('locations.form.fields.addressRegion', 'Region'), type: 'text' },
    { id: 'latitude', label: t('locations.form.fields.latitude', 'Latitude'), type: 'number', required: true },
    { id: 'longitude', label: t('locations.form.fields.longitude', 'Longitude'), type: 'number', required: true },
    {
      id: 'supportedModes',
      label: t('locations.form.fields.supportedModes', 'Supported modes'),
      type: 'select',
      options: modeOptions,
      multiple: true,
      listbox: true,
      required: true,
    },
    {
      id: 'capabilities',
      label: t('locations.form.fields.capabilities', 'Capabilities'),
      type: 'select',
      options: capabilityOptions,
      multiple: true,
      listbox: true,
    },
    { id: 'geofenceRadiusM', label: t('locations.form.fields.geofenceRadiusM', 'Geofence radius (m)'), type: 'number' },
    {
      id: 'slotBookingEnabled',
      label: t('locations.form.fields.slotBookingEnabled', 'Slot booking enabled'),
      type: 'checkbox',
    },
    {
      id: 'avgHandlingTimeMin',
      label: t('locations.form.fields.avgHandlingTimeMin', 'Average handling time (min)'),
      type: 'number',
    },
    { id: 'contactName', label: t('locations.form.fields.contactName', 'Contact name'), type: 'text' },
    { id: 'contactPhone', label: t('locations.form.fields.contactPhone', 'Contact phone'), type: 'text' },
    {
      id: 'gateInstructions',
      label: t('locations.form.fields.gateInstructions', 'Gate instructions'),
      type: 'textarea',
    },
    {
      id: 'operatingHoursJson',
      label: t('locations.form.fields.operatingHours', 'Operating hours (JSON)'),
      type: 'textarea',
      description: t(
        'locations.form.fields.operatingHoursHelp',
        'Optional JSON object like {\"mon\":{\"open\":\"06:00\",\"close\":\"22:00\"}}.',
      ),
    },
    { id: 'isActive', label: t('locations.form.fields.isActive', 'Active'), type: 'checkbox' },
  ], [capabilityOptions, modeOptions, t, typeOptions])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'identity',
      title: t('locations.form.groups.identity', 'Identity'),
      fields: ['name', 'code', 'unlocode', 'type'],
    },
    {
      id: 'address',
      title: t('locations.form.groups.address', 'Address & coordinates'),
      fields: [
        'addressStreet',
        'addressCity',
        'addressPostalCode',
        'addressCountry',
        'addressRegion',
        'latitude',
        'longitude',
      ],
    },
    {
      id: 'operations',
      title: t('locations.form.groups.operations', 'Operational profile'),
      fields: ['supportedModes', 'capabilities', 'geofenceRadiusM', 'slotBookingEnabled', 'avgHandlingTimeMin'],
    },
    {
      id: 'contact',
      title: t('locations.form.groups.contact', 'Contact & notes'),
      fields: ['contactName', 'contactPhone', 'gateInstructions', 'operatingHoursJson', 'isActive'],
    },
  ], [t])

  return (
    <CrudForm<LocationFormValues>
      title={title}
      submitLabel={submitLabel}
      backHref={backHref}
      cancelHref={cancelHref}
      initialValues={initialValues}
      schema={locationFormSchema}
      fields={fields}
      groups={groups}
      onSubmit={onSubmit}
      onDelete={onDelete}
      isLoading={isLoading}
      loadingMessage={loadingMessage}
    />
  )
}

export default LocationCrudForm
